import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { Download, MapPin, Calendar, User, FileText, FileCheck, CheckCircle, Clock, SendHorizontal, MessageSquare, ChevronDown, Check, X, Send, PlusCircle, Wifi, WifiOff } from 'lucide-react';
// import ExportButton from '../../sharedComponents/ExportButton';
import { useNotification } from '../../sharedComponents/Notifications';
import Drawer from '../../sharedComponents/Drawer';
import Pagination from '../components/Pagination';
import {
    submissionService,
    categoryAdminService,
    cycleService,
    Submission,
    Category,
    Cycle,
    GeoPoliticalZone,
    userService,
    UserItem
} from '../services/adminAuth';
import { communicationService, InternalMessage } from '../services/communicationService';
import signalRService, { ConnectionStatus } from '../../../services/signalRService';
import { FILE_BASE_URL } from '../../../api/axios';
import { downloadFileFromUrl } from '../../../utils/downloadHelper';
import { useAuth } from '../../../auth/hooks/auth';

function SubmissionsManagement() {
    const { showNotification } = useNotification();
    const { user, refreshUnread } = useAuth();
    const location = useLocation();
    const isCategoryAdmin = user?.role === 'thematicarealeader';

    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [downloading, setDownloading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    // Chat Drawer State
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<InternalMessage[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [additionalDetails, setAdditionalDetails] = useState<string>('');
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(ConnectionStatus.Disconnected);
    // Track which submissionIds have unread messages (green dot)
    const [unreadSubmissionIds, setUnreadSubmissionIds] = useState<Set<string>>(new Set());

    // Lookups for Chat
    const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);
    const [roleUsers, setRoleUsers] = useState<{ id: string; name: string }[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const messageTemplates = [
        { id: 't1', label: 'Recommendation for new reviewer addition' },
        { id: 't2', label: 'Submission requires additional screening' },
        { id: 't3', label: 'Requesting feedback on current assessment' },
        { id: 't4', label: 'Proceeding with final approval' },
        { id: 't5', label: 'Rejecting due to technical inaccuracies' },
        { id: 't6', label: 'Assigning to a different thematic area' },
        { id: 't7', label: 'Other' }
    ];

    // Dropdown data
    const [, setCategories] = useState<Category[]>([]);
    const [, setCycles] = useState<Cycle[]>([]);
    const [, setZones] = useState<GeoPoliticalZone[]>([]);
    const [initialized, setInitialized] = useState(false);
    // Track whether async init has completed before reactive fetching begins
    const [hasInitialFetch, setHasInitialFetch] = useState(false);
    const [activeCycle, setActiveCycle] = useState<Cycle | null>(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filter state
    const [filters, setFilters] = useState({
        search: '',
        categoryId: 0,
        cycleId: 0,
        authorId: '00000000-0000-0000-0000-000000000000',
        submittedFrom: null as string | null,
        submittedTo: null as string | null,
        applicationType: 0,
        geoPoliticalZoneId: 0
    });

    // SignalR state listener for local UI
    useEffect(() => {
        const unsubState = signalRService.onStateChange((status) => {
            setConnectionStatus(status);
        });

        // Join group when chat opens for a specific submission
        if (isChatOpen && selectedSubmission?.id && connectionStatus === ConnectionStatus.Connected) {
            signalRService.joinGroup(selectedSubmission.id);
        }

        const unsubscribe = signalRService.onReceiveMessage((data: any) => {
            const msgSubId = data.submissionId || data.SubmissionId;
            const msgId = data.id || data.Id;

            const newMsg: InternalMessage = {
                id: msgId || Date.now().toString(),
                message: data.message || data.Message,
                senderName: data.senderName || data.SenderName || 'System',
                senderUserId: data.senderUserId || data.SenderUserId,
                sendDateTime: data.sendDateTime || data.SendDateTime || new Date().toISOString(),
                receiverName: data.receiverName || data.ReceiverName
            };

            // If chat is open for this submission, append immediately
            if (isChatOpen && selectedSubmission && msgSubId === selectedSubmission.id) {
                setChatMessages(prev => {
                    // Avoid duplicates if the message was already added optimistically
                    if (prev.some(m => m.id === newMsg.id)) return prev;
                    return [...prev, newMsg];
                });
            } else if (msgSubId) {
                // Otherwise mark it as unread (green dot)
                setUnreadSubmissionIds(prev => new Set(prev).add(msgSubId));
            }
        });

        return () => {
            // Leave group when chat closes or submission changes
            if (selectedSubmission?.id && connectionStatus === ConnectionStatus.Connected) {
                signalRService.leaveGroup(selectedSubmission.id);
            }
            unsubscribe();
            unsubState();
        };
    }, [isChatOpen, selectedSubmission?.id, connectionStatus]);

    // ── Init data ──────────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            await fetchDropdownData();
            await fetchRoles();
            try {
                const response = await cycleService.getRunningCycle();
                if (response.isSuccess && response.data) {
                    const runningCycle = response.data;
                    setActiveCycle(runningCycle);
                    setFilters(prev => ({ ...prev, cycleId: runningCycle.id }));
                    // Fetch submissions immediately with running cycle
                    await fetchSubmissions(runningCycle.id);
                    setHasInitialFetch(true);
                } else {
                    setHasInitialFetch(true);
                }
            } catch (err) {
                setHasInitialFetch(true);
            } finally {
                setInitialized(true);
            }
        };
        init();
    }, []);

    // Handle deep link to chat from global notification
    useEffect(() => {
        if (!initialized || submissions.length === 0) return;

        const params = new URLSearchParams(location.search);
        const openChatId = params.get('openChat');

        if (openChatId) {
            const targetSub = submissions.find(s => s.id === openChatId);
            if (targetSub) {
                handleOpenChat(targetSub);
                // Clear the param from URL
                window.history.replaceState({}, '', location.pathname);
            }
        }
    }, [location.search, submissions, initialized]);

    useEffect(() => {
        if (!initialized) return;
        // Skip initial fetch if activeCycle exists and hasn't been fetched yet
        if (activeCycle && !hasInitialFetch) return;

        const handler = setTimeout(() => {
            fetchSubmissions();
        }, 300);
        return () => clearTimeout(handler);
    }, [currentPage, pageSize, filters, initialized, activeCycle, hasInitialFetch]);

    const fetchDropdownData = async () => {
        try {
            const [catsRes, cyclesRes, zonesRes] = await Promise.all([
                submissionService.getAllCategories(),
                cycleService.getAllCycles(),
                submissionService.getGeoPoliticalZones()
            ]);

            if (catsRes.isSuccess && catsRes.data) {
                setCategories(catsRes.data);
                if (isCategoryAdmin) {
                    const fetchedCategoriesRes = await categoryAdminService.getCategoriesForCategoryAdmin();
                    if (fetchedCategoriesRes.isSuccess && fetchedCategoriesRes.data && fetchedCategoriesRes.data.length > 0) {
                        const assignedId = fetchedCategoriesRes.data[0].id;
                        setFilters(prev => ({ ...prev, categoryId: assignedId }));
                    }
                }
            }
            if (cyclesRes.isSuccess && cyclesRes.data) setCycles(cyclesRes.data);
            if (zonesRes.isSuccess && zonesRes.data) setZones(zonesRes.data);
        } catch (err) {
            }
    };

    const fetchRoles = async () => {
        try {
            const rolesRes = await userService.getAdminRoles();
            if (rolesRes.isSuccess && rolesRes.data) {
                setRoles(rolesRes.data.map(r => ({ id: r.id.toString(), name: r.name })));
            }
        } catch (err) {
            }
    };

    const fetchUsersByRole = async (roleId: string) => {
        setLoadingUsers(true);
        try {
            const res = await userService.getUsersByRoleId(Number(roleId));
            if (res.isSuccess && res.data) {
                setRoleUsers(res.data.map(u => ({
                    id: u.userId,
                    name: u.person?.fullName || u.username
                })));
            }
        } catch (err) {
            } finally {
            setLoadingUsers(false);
        }
    };

    const fetchChatHistory = async (submissionId: string) => {
        setLoadingHistory(true);
        try {
            const res = await communicationService.getMessageHistory(submissionId);
            if (res.isSuccess && res.data) {
                const sortedMessages = [...res.data].sort((a, b) =>
                    new Date(a.sendDateTime).getTime() - new Date(b.sendDateTime).getTime()
                );
                setChatMessages(sortedMessages);
            }
        } catch (err) {
            } finally {
            setLoadingHistory(false);
        }
    };

    const fetchSubmissions = async (cycleIdOverride?: number) => {
        const cycleId = cycleIdOverride !== undefined ? cycleIdOverride : Number(filters.cycleId);

        if (!cycleId || cycleId === 0) {
            return;
        }

        setLoading(true);
        try {
            const response = await submissionService.getAllSubmissions({
                pageNumber: currentPage,
                pageSize,
                sortBy: 'SubmittedAt',
                sortDescending: true,
                search: filters.search,
                categoryId: filters.categoryId,
                cycleId: cycleId,
                authorId: filters.authorId,
                submittedFrom: filters.submittedFrom,
                submittedTo: filters.submittedTo,
                applicationType: 0,
                geoPoliticalZoneId: 0
            });

            if (response.isSuccess && response.data) {
                setSubmissions(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            }
        } catch (err) {
            } finally {
            setLoading(false);
        }
    };


    const handleSearch = (query: string) => {
        setFilters(prev => ({ ...prev, search: query }));
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => { setPageSize(size); setCurrentPage(1); };

    const handleOpenChat = async (submission: Submission) => {
        setSelectedSubmission(submission);
        setIsChatOpen(true);
        // Clear unread dot for this submission
        setUnreadSubmissionIds(prev => {
            const next = new Set(prev);
            next.delete(submission.id);
            return next;
        });

        // Sync read status with server
        try {
            await communicationService.markAsRead(submission.id);
            if (refreshUnread) await refreshUnread();
        } catch (err) {
            }

        fetchChatHistory(submission.id);
    };

    const handleCloseChat = () => {
        setIsChatOpen(false);
        setChatMessages([]);
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedUsers.length === 0 || !selectedSubmission || !additionalDetails.trim()) return;

        setSendingMessage(true);
        try {
            const payload = {
                submissionId: selectedSubmission.id,
                receiverId: selectedUsers.join(','),
                message: additionalDetails.trim()
            };
            const res = await communicationService.sendMessage(payload);
            
            if (res.isSuccess) {
                setIsModalOpen(false);
                setAdditionalDetails('');
                setSelectedUsers([]);
                setSelectedRoleId('');
                showNotification('success', { title: 'Message Sent', message: 'Your message has been delivered.' });
            }
        } catch (err) {
            showNotification('error', { title: 'Send Failed', message: 'Could not deliver your message.' });
        } finally {
            setSendingMessage(false);
        }
    };

    const columns: TableColumn<Submission>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: (value) => <span className="text-gray-500 text-sm font-mono">{value}</span>
        },
        {
            key: 'title',
            label: 'Title / Author',
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900 mb-1">{row.title}</div>
                    <div className="text-sm text-gray-500">{row.authors}</div>
                </div>
            )
        },
        {
            key: 'category',
            label: 'Thematic Area',
            render: (value, row) => (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                    {row.category?.name || 'N/A'}
                </span>
            )
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => {
                const statusStr = String(value).toLowerCase().replace(/\s/g, '');
                
                const statusMap: { [key: string]: { label: string; className: string } } = {
                    '1': { label: 'Submitted', className: 'bg-blue-50 text-blue-700' },
                    'submitted': { label: 'Submitted', className: 'bg-blue-50 text-blue-700' },
                    '2': { label: 'Under Review', className: 'bg-amber-50 text-amber-700' },
                    'underreview': { label: 'Under Review', className: 'bg-amber-50 text-amber-700' },
                    '3': { label: 'Returned', className: 'bg-orange-50 text-orange-700' },
                    'returned': { label: 'Returned', className: 'bg-orange-50 text-orange-700' },
                    '4': { label: 'Rejected', className: 'bg-red-50 text-red-700' },
                    'rejected': { label: 'Rejected', className: 'bg-red-50 text-red-700' },
                    '5': { label: 'Approved', className: 'bg-green-50 text-green-700' },
                    'approved': { label: 'Approved', className: 'bg-green-50 text-green-700' },
                };

                const config = statusMap[statusStr] || { label: String(value), className: 'bg-gray-100 text-gray-700' };
                return <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${config.className}`}>{config.label}</span>;
            }
        }
    ];

    const isDESSOrTagChairman = user?.roleId === 9 || user?.roleId === 5 || user?.role === 'dess';

    const actions: TableAction<Submission>[] = [
        {
            icon: <FileText size={18} />,
            title: "View Details",
            onClick: (row) => { setSelectedSubmission(row); setIsDetailsOpen(true); },
            className: "text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition-colors"
        },
        ...(!isDESSOrTagChairman ? [{
            icon: (
                <span className="relative inline-flex">
                    <MessageSquare size={18} />
                    {unreadSubmissionIds.has('__ROW_ID__') && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                    )}
                </span>
            ),
            title: "Discuss (Internal)",
            onClick: (row: Submission) => handleOpenChat(row),
            className: "text-[#088A61] hover:bg-[#088A61]/10 p-2 rounded-lg transition-colors"
        }] : [])
    ];

    // Build per-row actions with correct unread state
    const getRowActions = (row: Submission): TableAction<Submission>[] => [
        {
            icon: <FileText size={18} />,
            title: "View Details",
            onClick: () => { setSelectedSubmission(row); setIsDetailsOpen(true); },
            className: "text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition-colors"
        },
        ...(!isDESSOrTagChairman ? [{
            icon: (
                <span className="relative inline-flex">
                    <MessageSquare size={18} />
                    {unreadSubmissionIds.has(row.id) && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white animate-pulse" />
                    )}
                </span>
            ),
            title: "Discuss (Internal)",
            onClick: () => handleOpenChat(row),
            className: "text-[#088A61] hover:bg-[#088A61]/10 p-2 rounded-lg transition-colors"
        }] : [])
    ];

    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Submissions Management</h1>
                    <p className="text-gray-500 mt-1 font-medium italic">Monitor, review, and track the progress of all thematic area submissions.</p>
                </div>
                {/* <ExportButton reportType={2} parameters={{ cycleId: filters.cycleId, categoryId: filters.categoryId }} /> */}
            </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <ReusableTable
                    title="All Submissions"
                    subTitle={`Showing ${totalRecords} submissions`}
                    columns={columns}
                    data={submissions}
                    getActions={getRowActions}
                    loading={loading}
                    showSearch={true}
                    onSearch={handleSearch}
                    searchPlaceholder="Search by ID, title, author..."
                />
                <div className="p-6 bg-gray-50/50 border-t border-gray-100">
                    <Pagination
                        currentPage={currentPage} totalPages={totalPages}
                        onPageChange={handlePageChange} pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange} totalRecords={totalRecords}
                    />
                </div>
            </div>

            {/* Chat Drawer */}
            <Drawer
                isOpen={isChatOpen}
                onClose={handleCloseChat}
                title={`Discussion: ${selectedSubmission?.title}`}
                width="max-w-xl"
            >
                <div className="flex flex-col h-full -m-6">

                    {/* Submission context header — clearly separate from chat */}
                    <div className="bg-gradient-to-r from-[#088A61]/10 to-emerald-50 border-b border-[#088A61]/20 px-5 py-3.5">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold text-[#088A61] uppercase tracking-widest mb-0.5">Submission</p>
                                <p className="text-sm font-bold text-gray-900 line-clamp-1">{selectedSubmission?.title}</p>
                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{selectedSubmission?.authors}</p>
                            </div>
                            <div className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${connectionStatus === ConnectionStatus.Connected
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : connectionStatus === ConnectionStatus.Reconnecting
                                    ? 'bg-yellow-50 text-yellow-700 border-yellow-200 animate-pulse'
                                    : 'bg-red-50 text-red-700 border-red-200 animate-pulse'
                                }`}>
                                {connectionStatus === ConnectionStatus.Connected ? <Wifi size={10} /> : <WifiOff size={10} />}
                                {connectionStatus === ConnectionStatus.Connected ? 'Live'
                                    : connectionStatus === ConnectionStatus.Reconnecting ? 'Reconnecting'
                                        : 'Connecting'}
                            </div>
                        </div>
                    </div>

                    {/* Chat messages */}
                    <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3 bg-gray-50">
                        {loadingHistory ? (
                            <div className="flex flex-col items-center justify-center h-full space-y-3">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#088A61]"></div>
                                <p className="text-sm text-gray-500 font-medium">Loading messages...</p>
                            </div>
                        ) : chatMessages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-center p-8 space-y-3">
                                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                                    <MessageSquare size={28} className="text-gray-300" />
                                </div>
                                <p className="text-sm font-semibold text-gray-400">No messages yet</p>
                                <p className="text-xs text-gray-400">Start a discussion using the button below.</p>
                            </div>
                        ) : (
                            chatMessages.map((msg) => {
                                const myName = user?.name?.toLowerCase().trim() || '';
                                const senderName = msg.senderName?.toLowerCase().trim() || '';
                                const isMine = (msg.senderUserId && msg.senderUserId === user?.id) ||
                                    (senderName === myName && myName !== '');
                                return (
                                    <div key={msg.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                                        {/* Sender label */}
                                        <div className={`flex items-center gap-1.5 mb-1 px-1 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 ${isMine ? 'bg-[#088A61]' : 'bg-gray-400'
                                                }`}>
                                                {msg.senderName?.charAt(0)?.toUpperCase()}
                                            </div>
                                            <span className="text-[11px] font-semibold text-gray-600">
                                                {isMine ? 'You' : msg.senderName}
                                            </span>
                                            <span className="text-[10px] text-gray-400">
                                                {new Date(msg.sendDateTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                            </span>
                                        </div>

                                        {/* Bubble */}
                                        <div className={`max-w-[80%] px-4 py-3 rounded-2xl shadow-sm text-sm leading-relaxed whitespace-pre-wrap ${isMine
                                            ? 'bg-[#088A61] text-white rounded-tr-sm'
                                            : 'bg-white text-gray-800 border border-gray-100 rounded-tl-sm'
                                            }`}>
                                            {msg.message}
                                        </div>

                                        {/* To: label */}
                                        {msg.receiverName && (
                                            <div className="mt-1 px-1 flex items-center gap-1">
                                                <span className={`text-[10px] font-medium ${isMine ? 'text-[#088A61]/70' : 'text-gray-400'
                                                    }`}>
                                                    To: {msg.receiverName}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Compose footer */}
                    <div className="p-4 bg-white border-t border-gray-100 flex justify-center">
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="flex items-center gap-2 bg-[#088A61] text-white px-7 py-3 rounded-full text-sm font-bold shadow-lg shadow-[#088A61]/20 hover:bg-[#077a55] hover:scale-105 active:scale-95 transition-all"
                        >
                            <PlusCircle size={18} />
                            New Message
                        </button>
                    </div>
                </div>
            </Drawer>

            {/* New Discussion Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg relative z-10 overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <h3 className="text-lg font-bold text-gray-900 truncate pr-4">
                                Internal Discussion: <span className="text-[#088A61]">{selectedSubmission?.title}</span>
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Select Role:</label>
                                    <select
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-[#088A61] outline-none"
                                        value={selectedRoleId}
                                        onChange={(e) => {
                                            const roleId = e.target.value;
                                            setSelectedRoleId(roleId);
                                            setSelectedUsers([]);
                                            if (roleId) fetchUsersByRole(roleId);
                                            else setRoleUsers([]);
                                        }}
                                    >
                                        <option value="">Select a Role</option>
                                        {roles.map(role => (
                                            <option key={role.id} value={role.id}>{role.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {selectedRoleId && (
                                    <div className="relative">
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Select User(s):</label>
                                        <div
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm cursor-pointer flex justify-between items-center"
                                            onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                                        >
                                            <span className="truncate text-gray-700 font-medium">
                                                {loadingUsers ? 'Loading...' : selectedUsers.length === 0 ? "Select Recipient" : roleUsers.find(u => u.id === selectedUsers[0])?.name || "User Selected"}
                                            </span>
                                            <ChevronDown size={16} className={`text-gray-400 transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                                        </div>

                                        {isUserDropdownOpen && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setIsUserDropdownOpen(false)}></div>
                                                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl z-20 max-h-60 overflow-y-auto p-2">
                                                    {roleUsers.map(user => (
                                                        <label key={user.id} className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded-xl mb-1 transition-colors ${selectedUsers.includes(user.id) ? 'bg-[#088A61]/5 border border-[#088A61]/20' : ''}`}>
                                                            <input
                                                                type="radio" name="chatUser" className="w-4 h-4 border-gray-300 text-[#088A61] focus:ring-[#088A61]"
                                                                checked={selectedUsers.includes(user.id)}
                                                                onChange={() => {
                                                                    setSelectedUsers([user.id]);
                                                                    setIsUserDropdownOpen(false);
                                                                }}
                                                            />
                                                            <span className={`text-sm ${selectedUsers.includes(user.id) ? 'font-bold text-[#088A61]' : 'text-gray-700 font-medium'}`}>{user.name}</span>
                                                        </label>
                                                    ))}
                                                    {roleUsers.length === 0 && !loadingUsers && <p className="p-3 text-xs text-gray-400 italic text-center">No users found</p>}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Compose Message:</label>
                                <textarea
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm focus:ring-2 focus:ring-[#088A61] outline-none min-h-[150px] resize-none"
                                    placeholder="Type your internal message here..."
                                    value={additionalDetails}
                                    onChange={(e) => setAdditionalDetails(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-200">Cancel</button>
                            <button
                                onClick={handleSendMessage}
                                disabled={selectedUsers.length === 0 || sendingMessage || !additionalDetails.trim()}
                                className={`px-8 py-2.5 rounded-xl text-sm font-bold shadow-lg transition-all flex items-center gap-2 ${selectedUsers.length > 0 && !sendingMessage && additionalDetails.trim()
                                    ? 'bg-[#088A61] text-white hover:scale-105 active:scale-95'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                            >
                                {sendingMessage ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <SendHorizontal size={18} />
                                )}
                                {sendingMessage ? 'Sending...' : 'Send Message'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Submission Details Drawer */}
            <Drawer
                isOpen={isDetailsOpen} onClose={() => setIsDetailsOpen(false)}
                title={selectedSubmission ? `Submission Details: ${selectedSubmission.submissionNumber}` : 'Details'}
                width="max-w-2xl"
            >
                {selectedSubmission && (
                    <div className="space-y-8 p-1">
                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">{selectedSubmission.title}</h2>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-[#088A61]/10 rounded-full flex items-center justify-center text-[#088A61] font-bold">
                                    {selectedSubmission.author?.person?.fullName?.charAt(0) || <User size={20} />}
                                </div>
                                <div>
                                    <div className="text-sm font-bold text-gray-900">{selectedSubmission.author?.person?.fullName || 'N/A'}</div>
                                    <div className="text-sm text-gray-500">Corresponding Author</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Submitted At</p>
                                    <p className="font-medium text-gray-900">{new Date(selectedSubmission.submittedAt).toLocaleDateString()}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Category</p>
                                    <p className="font-medium text-gray-900">{selectedSubmission.category?.name || 'N/A'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4 border-t border-gray-100">
                            <button
                                disabled={downloading}
                                onClick={async () => {
                                    if (selectedSubmission.manuscriptUrl) {
                                        setDownloading(true);
                                        const url = selectedSubmission.manuscriptUrl.startsWith('http') ? selectedSubmission.manuscriptUrl : `${FILE_BASE_URL}/${selectedSubmission.manuscriptUrl}`;
                                        try { await downloadFileFromUrl(url, `${selectedSubmission.submissionNumber}_manuscript.pdf`); } finally { setDownloading(false); }
                                    }
                                }}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3.5 bg-[#088A61] text-white rounded-xl font-bold hover:bg-[#066d4d] shadow-lg shadow-[#088A61]/20 ${downloading ? 'opacity-75' : ''}`}
                            >
                                <Download className={`w-5 h-5 ${downloading ? 'animate-pulse' : ''}`} />
                                {downloading ? 'Downloading...' : 'Download Manuscript'}
                            </button>
                        </div>
                    </div>
                )}
            </Drawer>
        </div>
    );
}

export default SubmissionsManagement;